/**
 * Titans "Learning by Surprise" — Core Surprise Calculator
 *
 * Implements surprise-based learning inspired by:
 * - Google Titans architecture (gradient-based surprise at test time)
 * - Surplexity metric for model collapse prevention
 *
 * Consumes the new modular types from trainingTypes.ts.
 */

import type { SurpriseMetrics, SurpriseConfig } from './types';
import { DEFAULT_SURPRISE_CONFIG } from './types';
import type { DemoScenario, TrainingEvent, ContextPack } from '../../types/trainingTypes';

export class SurpriseCalculator {
    private config: SurpriseConfig;

    constructor(config: Partial<SurpriseConfig> = {}) {
        this.config = { ...DEFAULT_SURPRISE_CONFIG, ...config };
    }

    // ────────────────────────────────────────────
    // PUBLIC API
    // ────────────────────────────────────────────

    /**
     * Calculate surprise for a single DemoScenario using training history.
     */
    calculateSurprise(
        scenario: DemoScenario,
        history: TrainingEvent[],
        contextPack?: ContextPack
    ): SurpriseMetrics {
        const factors = {
            novelty: this.calcNovelty(scenario, history),
            complexity: this.calcComplexity(scenario),
            contextBreak: this.calcContextBreak(scenario, contextPack),
            edgeCaseScore: this.calcEdgeCase(scenario),
        };

        const score = Math.round(
            factors.novelty * this.config.weights.novelty +
            factors.complexity * this.config.weights.complexity +
            factors.contextBreak * this.config.weights.contextBreak +
            factors.edgeCaseScore * this.config.weights.edgeCaseScore
        );

        return {
            score: clamp(score, 0, 100),
            confidence: this.calcConfidence(history),
            factors,
            explanation: this.explain(factors, score),
        };
    }

    /**
     * Pick the best scenario from `scenarios` weighted by surprise.
     * Returns the chosen scenario and its metrics.
     */
    selectBySurprise(
        scenarios: DemoScenario[],
        history: TrainingEvent[],
        contextPack?: ContextPack
    ): { scenario: DemoScenario; surprise: SurpriseMetrics } {
        if (!this.config.enabled || scenarios.length === 0) {
            const idx = Math.floor(Math.random() * scenarios.length);
            return {
                scenario: scenarios[idx],
                surprise: this.calculateSurprise(scenarios[idx], history, contextPack),
            };
        }

        const scored = scenarios.map(s => ({
            scenario: s,
            surprise: this.calculateSurprise(s, history, contextPack),
        }));

        // Weighted random: blend surprise with randomness
        const weights = scored.map(({ surprise }) => {
            const surpriseWeight = surprise.score / 100;
            const random = Math.random();
            return surpriseWeight * this.config.surpriseBias +
                random * (1 - this.config.surpriseBias);
        });

        const totalWeight = weights.reduce((a, b) => a + b, 0);
        let roll = Math.random() * totalWeight;

        for (let i = 0; i < scored.length; i++) {
            roll -= weights[i];
            if (roll <= 0) return scored[i];
        }

        return scored[0]; // fallback
    }

    // ────────────────────────────────────────────
    // FACTOR CALCULATORS
    // ────────────────────────────────────────────

    private calcNovelty(scenario: DemoScenario, history: TrainingEvent[]): number {
        if (history.length === 0) return 100;

        const words = tokenize(scenario.prompt);
        let maxSimilarity = 0;

        for (const event of history.slice(-20)) {
            const eventWords = tokenize(event.caseTitle + ' ' + (event.feedbackNote || ''));
            const inter = [...words].filter(w => eventWords.has(w)).length;
            const union = new Set([...words, ...eventWords]).size;
            maxSimilarity = Math.max(maxSimilarity, union > 0 ? inter / union : 0);
        }

        return Math.round((1 - maxSimilarity) * 100);
    }

    private calcComplexity(scenario: DemoScenario): number {
        let c = 0;
        const issues = scenario.baselineIssues.length + scenario.replicaIssues.length;
        c += Math.min(40, issues * 8);
        const len = scenario.prompt.length + scenario.baselineResponse.length;
        c += Math.min(30, (len / 100) * 5);
        const diff = Math.abs(scenario.baselineResponse.length - scenario.replicaResponse.length);
        c += Math.min(30, (diff / 50) * 3);
        return Math.round(clamp(c, 0, 100));
    }

    private calcContextBreak(scenario: DemoScenario, contextPack?: ContextPack): number {
        let cb = 0;
        if (scenario.guardianTriggered) cb += 50;
        cb += Math.min(30, scenario.baselineIssues.length * 6);
        if (contextPack) {
            const active = Object.values(contextPack).filter(Boolean).length;
            if (active > 0 && scenario.baselineIssues.length > 0) cb += 20;
        }
        return Math.round(clamp(cb, 0, 100));
    }

    private calcEdgeCase(scenario: DemoScenario): number {
        let e = 0;
        if (scenario.guardianTriggered) e += 60;
        if (scenario.baselineIssues.length >= 3 && scenario.replicaIssues.length <= 1) e += 30;
        const edgeKeywords = ['outside', 'exception', 'unusual', 'rare', 'special', 'override'];
        if (scenario.baselineIssues.some(i => edgeKeywords.some(k => i.toLowerCase().includes(k)))) {
            e += 10;
        }
        return Math.round(clamp(e, 0, 100));
    }

    private calcConfidence(history: TrainingEvent[]): number {
        const n = history.length;
        if (n === 0) return 0.3;
        if (n < 5) return 0.5;
        if (n < 10) return 0.7;
        if (n < 20) return 0.85;
        return 0.95;
    }

    // ────────────────────────────────────────────
    // EXPLANATION GENERATOR
    // ────────────────────────────────────────────

    private explain(factors: SurpriseMetrics['factors'], score: number): string[] {
        const lines: string[] = [];

        if (score >= 80) lines.push('🔥 Highly surprising — excellent learning opportunity');
        else if (score >= 60) lines.push('⚡ Moderately surprising — good training value');
        else if (score >= 40) lines.push('📝 Somewhat familiar — useful for reinforcement');
        else lines.push('✓ Very familiar — low learning value');

        if (factors.novelty > 70) lines.push('Novel situation not seen in recent training');
        if (factors.complexity > 70) lines.push('Complex scenario with multiple considerations');
        if (factors.contextBreak > 70) lines.push('Breaks expected patterns — requires attention');
        if (factors.edgeCaseScore > 70) lines.push('Likely edge case — high learning value');

        return lines;
    }
}

// ────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────

function clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
}

function tokenize(text: string): Set<string> {
    return new Set(
        text.toLowerCase().split(/\s+/).filter(w => w.length > 3)
    );
}

/** Singleton instance */
export const surpriseCalculator = new SurpriseCalculator();
